<template>
  <v-container v-if='loaded'
    fill-height
    fluid
    grid-list-xl>

    <v-dialog v-model="openAuditTrail" max-width="1200px">
      <app-audit-trail-timeline entity="disposed" v-if='openAuditTrail' :entity_id="disposed_id" :fields='config.fields'/>
    </v-dialog>

    <v-flex>
      <app-upsert
        ref='upsert'
        :config="config"
        :item="disposed"
        :title="`Destroy request - ${disposed.protocol.name}`"
        :subtitle="`${disposed.status} - ${disposed.date}`"
        :hide-save="true">

        <template slot='headerActions'>
          <app-audit-trail-btn @click="openAuditTrail = true" />

          <app-patch v-if='disposed.id && isAdmin' entity="disposed" :entity_id="disposed.id" :protocol_id="disposed.protocol_id"/>

          <v-btn
            color="red"
            @click="$router.push('/disposed')">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
        </template>

        <template v-slot:actions="{ item }">

          <v-btn
            v-if="disposed.pdf && ['DOCUMENTED', 'VALIDATED', 'CANCELED'].includes(disposed.status)"
            class="mx-1"
            color="purple darken-2 white--text"
            :href='`${BASE_URL}/pdf/${disposed.pdf}`'>
            DOCUMENTATION
              <v-icon right light>mdi-file-pdf-box</v-icon>
          </v-btn>

          <v-btn
            v-if="disposed.status !== 'CANCELED' && disposed.pdf && (user.role === 'Administrator' || user.role === 'EMN User' || user.role === 'Monitor')"
            class="mx-1 font-weight-light"
            color="primary"
            :loading="loading"
            :disabled="!nDrugs || loading || !item._pdf"
            @click="saveNewPdf(item)">SAVE NEW PDF</v-btn>


          <v-btn
            v-if="disposed.status === 'DOCUMENTED' && isAdmin"
            id="validate_btn"
            class="mx-1 font-weight-light"
            color="primary"
            :loading="loading"
            :disabled="!nDrugs || loading"
            @click="validate(item)">VALIDATE</v-btn>

          <v-btn
            v-if="['REQUESTED', 'DOCUMENTED'].includes(disposed.status) && isAdmin"
            class="mx-1 red white--text font-weight-light"
            :loading="loading"
            :disabled="!nDrugs || loading"
            @click="cancel">CANCEL</v-btn>

            <v-btn
            v-if="canUpdate"
            class="mx-1 font-weight-light"
            color="primary"
            :loading="loading"
            :disabled="loading"
            @click="$refs.upsert.save()">UPDATE</v-btn>

          <v-btn
            v-if="['INIT', 'REQUESTED', 'IMPORTED'].includes(disposed.status)"
            id="request_btn"
            class="mx-1 font-weight-light"
            color="primary"
            :loading="loading"
            :disabled="!nDrugs || loading"
            @click="request(item)">{{disposed.status === 'REQUESTED' ? 'CONFIRM' : 'REQUEST'}}</v-btn>

          <app-table
            :hide-upsert="disposed.status !== 'INIT'"
            :title="disposed.status === 'INIT' ? 'Drugs to destroy' : 'Drugs destroyed' "
            :config="drugConfig"
            upsert-max-width="850px"
            @partially="partiallyDetails"
            @saved="saved"
            @loaded="loadedDrugs"
            :filters="{ disposed_id }"
            no-search
            :hidden-fields="{ disposed_id, protocol_id: disposed.protocol_id, parent_site_id: disposed.site_id, storage_id: disposed.storage_id, disposal_id: disposed.disposal_id, destroy_expired: disposed.destroy_expired }" />

          <v-dialog
            v-model='openPartially'
            persistent
            max-width='750px'>
            <material-card
              title="Partially used details"
              :text="`${selectedPartially.drug && selectedPartially.drug.name + ' (' + selectedPartially.drug.packaging + ' ' + selectedPartially.drug.dose + selectedPartially.drug.measure_unit}) - ${selectedPartially.lotto}`" color='primary'>
              <v-form
                class="mt-4"
                ref="form">
                <v-data-table dense
                  :headers="partiallyHeaders"
                  :footer-props="{ 'items-per-page-options': [5, 10] }"
                  :items="partiallyItems">
                  <template v-slot:item.unused="props">
                    <v-text-field
                      v-if='disposed.status === "INIT" && selectedPartially.type !== "RETURNED" '
                      v-model="props.item.unused"
                      label="n. Unused"
                      type='number'
                      :rules='[validators.natural()]'
                      hide-details
                      single-line
                      autofocus
                    ></v-text-field>
                    <span v-else v-text="props.item.unused" />
                  </template>
                </v-data-table>

                <v-flex
                  xs12
                  text-right>
                  <v-btn class="mx-1 font-weight-light"
                    color="primary"
                    v-if='disposed.status === "INIT" && selectedPartially.type !== "RETURNED"'
                    :disabled="!enablePartially || loading"
                    @click="confirmPartially">CONFIRM</v-btn>
                  <v-btn v-else class="mx-1 font-weight-light" color='warning' @click='openPartially=false'>CLOSE</v-btn>
                  
                </v-flex>
              </v-form>
            </material-card>
          </v-dialog>

        </template>

      </app-upsert>

    </v-flex>

  </v-container>
</template>

<script>
/* eslint-disable no-case-declarations */

import config from './config_index.js'
import drugConfig from './drug_index.js'
import validators from '@/utils/validators'
const { getDetail, getList, upsert } = require('@/api/base_entity')
const { validateDisposed, requestDisposed, cancelDisposed } = require('@/api/disposed')
import serialHelpers from '../../helpers'
import { mapState } from 'vuex'

const BASE_URL = process.env.VUE_APP_BASE_API

export default {
  data () {
    return {
      validators,
      BASE_URL,
      config,
      drugConfig,
      disposed_id: null,
      nDrugs: 0,
      drugs: [],
      disposed: {},
      loaded: false,
      loading: false,
      openPartially: false,
      selectedPartially: {},
      partiallyItems: [],
      partiallyHeaders: [
        { text: 'Serial n.', value: 'serial' },
        { text: 'N.unused', value: 'unused' }
      ],
      openAuditTrail: false
    }
  },
  async created () {
    this.disposed_id = Number(this.$route.params.disposedId)
    this.disposed = await getDetail('disposed', { id: this.disposed_id })
    this.loaded = true
  },
  computed: {
    ...mapState(['user']),
    isAdmin () {
      return this.user.role === 'Administrator' || this.user.role === 'EMN User'
    },
    enablePartially () {
      return !this.partiallyItems.find(p => Number(p.unused) === 0 || p.unused === '')
    },
    canUpdate(){
      return (this.isAdmin && this.disposed.status !=='INIT')
    }
  },  
  methods: {
    async cancel () {
      const ret = await this.$root.$confirm.open('Cancel destroy request', 'Are you sure to cancel this destroy request?', { color: 'red', cancelLabel: 'No' })
      if (!ret) return
      this.loading = true
      try {
        await cancelDisposed(this.disposed.id)
        this.$router.go()
      } catch (e) {
        this.$root.$emit('openSnackbar', {
          message: `Error: ` + e.toString(),
          color: 'error'
        })
      }
      this.loading = false
    },    
    async saveNewPdf (item) {
      this.loading = true
      const formData = new FormData()
      formData.append('pdf', item['_pdf'])
      try {
        await upsert(`disposed/save_new_pdf/${this.disposed_id}`, formData)
        this.$router.go()
      } catch (e) {
        this.$root.$emit('openSnackbar', {
          message: 'An error occurred during update. ' +
            e && e.response ? e.response.data.message : 'Error',
          color: 'error'
        })
        this.loading = false        
      }
    },    
    async missingDetails () {
      if (!this.disposed.site_id) {
        return false
      }
      // le drugs con dei partially used specificati devono
      // avere un corrispondente dettaglio specificato (solo bottle cps, blister e vials)
      // TODO: forse dovrei farlo nel backend ?
      const nPartiallyUsed = this.drugs.reduce((tot, item) => ['Bottle CPS', 'Blister', 'Vials'].includes(item.drug.packaging) ? tot + item.n_partially : tot, 0)
        // { no: this.drugs.map(d => ({ drug_id: d.drug.id, lotto: d.lotto })) }
      const fullPartiallyUsed = await getList('partiallyused_disposed', { disposed_id: this.disposed_id }, { rowsPerPage: null }).then(ret => ret.data)
      const nDetails = fullPartiallyUsed.reduce((tot, p) => tot + ((Number(p.unused) > 0) ? 1 : 0), 0)
      return nDetails !== nPartiallyUsed //!this.partiallyItems.find(p => Number(p.unused) === 0 || p.unused === '')
    },    
    saved ({ item }) {
      if (item.type !== 'RETURNED' && item.n_partially && ['Bottle CPS', 'Blister', 'Vials'].includes(item.drug.packaging)) {
        this.partiallyDetails(item)
      }
    },    
    loadedDrugs (items) {
      this.drugs = items
      this.nDrugs = items.length
    },
    async request (item) {
      if (!this.$refs.upsert.validate()) return
      this.loading = true

      // check if details are missing
      if (await this.missingDetails()) {
        this.$root.$emit('openSnackbar', {
          message: `Missing partially used details!`,
          color: 'error'
        })
        this.loading = false
        return
      }
      const formData = new FormData()
      formData.append('pdf', item['_pdf'])
      await requestDisposed(this.disposed_id, formData)
      this.$router.go()
    },
    async validate () {
      if (!this.$refs.upsert.validate()) return
      const ret = await this.$root.$confirm.open('Validate destroy request', 'Are you sure to validate this destroy request?', { color: 'red', cancelLabel: 'No' })
      if (!ret) return
      this.loading = true
      try {
        await validateDisposed(this.disposed_id)
        this.$router.go()
      } catch (e) {
        this.$root.$emit('openSnackbar', {
          message:  e?.response?.data?.message ?? e,
          color: 'error'
        })
        this.loading = false
      }
      this.loading = false
    },
    async confirmPartially () {
      const max_items = this.selectedPartially.drug.n_pieces
      const err = this.partiallyItems.find(p => p.unused >= max_items)
      if (err) {
        this.$root.$emit('openSnackbar', {
          message: `N.unused for serial ${err.serial} shoud be < of ${max_items} (${err.unused})`,
          color: 'error'
        })
        return
      }

      const updates = this.partiallyItems.map(p =>
       upsert('partiallyused_disposed', {
        // id: this.disposed_id + '_' + this.selectedPartilaly,
        // returned_id: this.selectedPartially.returned_id,
        disposed_id: this.disposed_id,
        lotto: this.selectedPartially.lotto,
        drug_id: this.selectedPartially.drug_id,
        protocol_id: this.disposed.protocol_id,
        site_id: this.disposed.site_id,
        serial: p.serial,
        unused: p.unused
      }))
      this.openPartially = false
      return Promise.all(updates)
    },
    async partiallyDetails (item) {
      this.selectedPartially = item

      // prepare what we need
      let partiallyItems = []

      // se e' returned, i partially used li copio da li'
      // let returned = {}
      // if (item.type === 'RETURNED') {
      //   returned = await getList('partiallyused_disposed', {
      //     returned_id: item.returned_id,
      //     lotto: item.lotto,
      //     drug_id: item.drug_id
      //   }, { rowsPerPage: 100 }  ).then(ret => ret.data)
      // }

      // se ho i seriali 
      if (item.serials_partially && item.serials_partially.length) {
        partiallyItems = serialHelpers.explodeSerials(item.serials_partially).map(serial => {
          return ({ serial, unused: 0 })
          // const returned_serial = returned.find(r => r.serial === String(serial))
          // if (returned_serial) {
          //   return ({ serial, unused: returned_serial.unused })
          // }
          // return ({ serial, unused: 0 })
        })
      } else {
        partiallyItems = Array.from({ length: item.n_partially }, (v, serial) => {
          return ({ serial: serial+1, unused: 0 })
          // const returned_serial = returned.find(r => r.serial === String(serial+1))
          // if (returned_serial) {
          //   return ({ serial: serial+1, unused: returned_serial.unused })
          // }
          // return ({ serial: serial+1, unused: 0 })
        })
      }

      const serverPartiallyItems = await getList('partiallyused_disposed', {
        disposed_id: this.disposed_id,
        lotto: item.lotto,
        drug_id: item.drug_id 
      }).then(ret => ret.data)
      if (serverPartiallyItems) {
        this.partiallyItems = partiallyItems.map(i => {
          const serverItem = serverPartiallyItems.find(d => d.serial === String(i.serial))
          if (serverItem) {
            return { serial: i.serial, unused: serverItem.unused }
          }
          return i
        })
      } else {
        this.partiallyItems = partiallyItems
      }

      this.openPartially = true
    },    
    // saved ({ data }) {
    //   this.request_id = data.id
    //   this.request = data
    //   this.isNew = false
    // },
  }
}
</script>
